import MXListProductCard from '@c/cards/MXListProductCard';
import MXProductCard from '@c/cards/MXProductCard';
import EmptyState from '@c/dashboard/EmptyState';
import { ShoppingBagIcon } from '@c/icons';
import ProductCardSkeleton, {
  ListProductCardSkeleton,
} from '@c/skeletons/ProductCardSkeleton';
import { ProductDocument } from '@models/product';
import Button from '@ui/Button';
import Paginator from '@ui/Paginator';
import { logEvent } from '@util/analytics';
import useBikes from '@util/hooks/useBikes';
import useLogWhenInView from '@util/hooks/useLogWhenInView';
import { PageSideFilters } from '@util/hooks/useProductsPage';
import { Pagination } from '@util/types/paginator';
import { OrderItemDocument } from 'models/order';
import { usePathname } from 'next/navigation';
import PageFilterPills from './PageFilterPills';

export const PageMainContent = ({
  view,
  isFetching,
  products,
  pageInfo,
  filters,
  controls,
  full,
  hidePaginator,
  filterState,
  onFiltersChange,
  attribution,
  setIsFetching,
  clearAllFilters,
}: {
  view: 'grid' | 'list' | 'map';
  isFetching: boolean;
  products: ProductDocument[];
  pageInfo: Pagination;
  filters: React.ReactNode;
  controls: React.ReactNode;
  full?: boolean;
  hidePaginator?: boolean;
  filterState: PageSideFilters;
  onFiltersChange: (filters: PageSideFilters) => void;
  attribution: OrderItemDocument['attribution'];
  setIsFetching?: (isFetching: boolean) => void;
  clearAllFilters: () => void;
}) => {
  const { bikeModels } = useBikes();
  const pathname = usePathname();
  const ref = useLogWhenInView(() =>
    logEvent('view_item_list', {
      item_list_id: `${pathname}`,
      item_list_name: `${pathname}`,
      item_category: `${pathname}`,
      items: products.map((p) => ({
        item_id: p.id,
        item_name: p.title,
      })),
    })
  );

  return (
    <div
      className={`flex w-full items-start gap-x-8 sm:mx-auto sm:max-w-content sm:py-16`}
    >
      {/* side filters*/}
      <div className="hidden lg:z-10 lg:block">{filters}</div>

      <div className={`w-full pb-[5rem] sm:mx-auto sm:max-w-content`} ref={ref}>
        <div className="mt-6 flex w-full gap-[0.9rem]  px-4  sm:mt-0 sm:px-0 lg:w-auto lg:gap-[1.6rem]">
          {controls}
        </div>
        {/* main content */}
        <div className="flex w-full gap-[2.2rem]">
          {/* controls */}
          {/* top filters and results */}
          <div className="flex h-full w-full flex-col lg:h-min">
            {Object.keys(filterState).some((f) => {
              if (f === 'rideTypes') {
                return false;
              }
              return Array.isArray(filterState[f as keyof typeof filterState])
                ? (filterState[f as keyof typeof filterState] as any).length
                : Boolean(filterState[f as keyof typeof filterState]);
            }) && (
              <div className="mt-8 flex flex-col-reverse justify-between gap-[2.4rem] overflow-x-hidden bg-brand-white px-8 pt-0 sm:px-0 lg:flex-row lg:flex-wrap lg:items-center lg:gap-0">
                <div className="flex w-full">
                  <PageFilterPills
                    filters={filterState}
                    handleFilterRemove={(filter) => {
                      onFiltersChange({
                        ...filterState,
                        [filter.key]: filter.value,
                        ...(filter.key === 'bikeMakes' && {
                          bikeModels: filterState.bikeModels?.filter((model) =>
                            bikeModels.includes(model)
                          ),
                        }),
                      });
                    }}
                    clearAllFilters={clearAllFilters}
                  />
                </div>
              </div>
            )}

            <div className={`h-full w-full sm:mt-8`}>
              {view === 'grid' ? (
                // grid view
                <div className="mt-8 grid h-full w-full grid-cols-2 gap-x-[1rem] gap-y-[0.5rem] px-4 pb-[1.6rem] sm:mx-auto sm:mt-0 sm:max-w-default sm:justify-start sm:gap-x-[0.25rem] sm:gap-y-[0.25rem] sm:overflow-hidden sm:pr-10 lg:grid-cols-[repeat(4,29.5rem)] lg:gap-y-[1.5rem] lg:px-0">
                  {isFetching ? (
                    Array(20)
                      .fill(0)
                      .map((_, index) => <ProductCardSkeleton key={index} />)
                  ) : products?.length && products?.length > 0 ? (
                    products.map((result, index) => (
                      <MXProductCard
                        priority={index === 0}
                        product={result}
                        key={result.id + index}
                        attribution={{
                          ...attribution,
                          section: 'category-page-grid',
                        }}
                      />
                    ))
                  ) : (
                    <div className="flex h-[30rem] w-[50rem] items-center justify-center sm:w-[120rem]">
                      <div className="flex flex-col items-center">
                        <div className="mb-5 text-center">
                          <EmptyState
                            icon={<ShoppingBagIcon />}
                            title={'No Results'}
                            description={
                              pathname?.includes('profile')
                                ? ''
                                : 'Have one to sell?'
                            }
                          />
                        </div>

                        {!pathname?.includes('profile') && (
                          <Button
                            text="Sell Yours"
                            type="tertiary"
                            href="/create-listing"
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : view === 'list' ? (
                // list view
                <div className="flex h-full flex-col justify-center gap-[0.8rem] px-[2.5rem] pb-[1.6rem] sm:mt-[2.4rem] sm:gap-[1.6rem] sm:px-0">
                  {isFetching ? (
                    <>
                      {Array(20)
                        .fill(0)
                        .map((_, index) => (
                          <ListProductCardSkeleton key={index} />
                        ))}
                    </>
                  ) : products?.length ? (
                    products.map((result) => {
                      return (
                        <>
                          <MXListProductCard
                            product={result}
                            key={result.id}
                            attribution={{
                              ...attribution,
                              section: 'category-page-list',
                            }}
                          />
                          <hr />
                        </>
                      );
                    })
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <div className="flex flex-col items-center">
                        <div className="mb-5 text-center">
                          <EmptyState
                            icon={<ShoppingBagIcon />}
                            title={'No Results'}
                            description={
                              pathname?.includes('profile')
                                ? ''
                                : 'Have one to sell?'
                            }
                          />
                        </div>

                        {!pathname?.includes('profile') && (
                          <Button
                            text="Sell Yours"
                            type="tertiary"
                            href="/create-listing"
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // <DirtBikeMap />
                <></>
              )}
            </div>

            {pageInfo.totalPages > 1 && !hidePaginator && (
              <Paginator
                currentPage={pageInfo?.page ?? 1}
                totalPages={pageInfo?.totalPages ?? 0}
                scroll={true}
                setIsFetching={setIsFetching}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
